import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonComponent, LoaderComponent, ModalUtils } from '@swissfex/sfx-commons';
import { NzModalService } from 'ng-zorro-antd/modal';

import { LOGIN_QUERY, LOGIN_QUERY_LOGOUT_REASON_MAP, LOGOUT_REASON, MAIN_ROUTES } from '../constants';
import { RouterService } from '../core/services/router.service';
import { SESSION_STORAGE_KEY, SessionStorageService } from '../core/services/session-storage.service';
import { TranslateRouterService } from '../core/services/translate-router.service';
import { MadTranslateService } from '../mad-translate/services/mad-translate.service';
import { AuthenticationService } from '../projects/services/authentication.service';

@Component({
    templateUrl: './logging-in.component.html',
    styleUrls: ['./logging-in.component.scss'],
    imports: [TranslatePipe, ButtonComponent, LoaderComponent]
})
export class LoggingInComponent implements OnInit, OnDestroy {
    private readonly authSvc = inject(AuthenticationService);
    private readonly sessionStorageSvc = inject(SessionStorageService);
    private readonly madTranslateSvc = inject(MadTranslateService);
    private readonly modalSvc = inject(NzModalService);
    private readonly router = inject(Router);
    private readonly routerSvc = inject(RouterService);
    private readonly translate = inject(TranslateService);
    private readonly translateRouterSvc = inject(TranslateRouterService);

    public hasWaitedAnExtendedPeriod = signal<boolean>(false);

    private readonly translatedRoute: string;
    private hasAwaitedAnExtendedPeriodTimeout: ReturnType<typeof setTimeout>;

    constructor() {
        this.translatedRoute = this.routerSvc.translateRoute([MAIN_ROUTES.LOGGING_IN], null, false);
    }

    public async ngOnInit(): Promise<void> {
        try {
            this.hasAwaitedAnExtendedPeriodTimeout = setTimeout(() => {
                this.hasWaitedAnExtendedPeriod.set(true);
            }, 5000);

            if (!this.authSvc.getAuthStatus().isAuthenticated) {
                await this.logoutUser();
                return;
            }
            const user = await this.authSvc.getCurrentUser();

            let returnUrl = this.sessionStorageSvc.get<string>(SESSION_STORAGE_KEY.RETURN_URL_KEY);
            if (returnUrl?.includes(this.translatedRoute)) {
                returnUrl = null;
            }
            if (returnUrl) {
                returnUrl = decodeURIComponent(returnUrl);
                const returnUrlUrlKey = LOGIN_QUERY.RETURN_URL;
                const returnUrlInReturnUrlIndex = returnUrl.lastIndexOf(returnUrlUrlKey);
                if (returnUrlInReturnUrlIndex > -1) {
                    returnUrl = returnUrl.substring(
                        returnUrlInReturnUrlIndex + returnUrlUrlKey.length + 1,
                        returnUrl.length
                    );
                    returnUrl = decodeURIComponent(returnUrl);
                }

                const uiLanguage = this.madTranslateSvc.getAppropriateUiLanguage(user);
                const translatedUrl = await this.translateRouterSvc.parseAndTranslateUrl(uiLanguage, returnUrl);
                await this.router.navigateByUrl(translatedUrl);
            } else {
                await this.redirectToProjects();
            }
        } catch (ex) {
            console.error(ex);
            const title = 'auth.login_error.title';
            const message = 'auth.login_error.message';

            const { isConfirmedPromise } = ModalUtils.createErrorModal(
                this.modalSvc,
                this.translate.instant(title),
                ex?.error?.message || this.translate.instant(message),
                'vertical-center-modal login-error-modal'
            );
            if (await isConfirmedPromise) {
                await this.logoutUser();
            }
        }
    }

    public ngOnDestroy() {
        if (this.hasAwaitedAnExtendedPeriodTimeout) {
            clearTimeout(this.hasAwaitedAnExtendedPeriodTimeout);
        }
    }

    public async redirectToProjects(): Promise<void> {
        await this.routerSvc.navigateToRoute([MAIN_ROUTES.PROJECTS]);
    }

    private async logoutUser(): Promise<void> {
        await this.routerSvc.navigateToLogout({
            queryParams: { [LOGIN_QUERY_LOGOUT_REASON_MAP[LOGOUT_REASON.FORBIDDEN]]: LOGOUT_REASON.FORBIDDEN }
        });
    }
}
